/**
 * The geometry of a radar chart, kept apart from the markup that draws it.
 *
 * Everything here is pure arithmetic on a unit-agnostic square: the caller says
 * how big the box is and how far the outermost ring sits from the centre, and
 * gets back points it can hand straight to SVG. No domain vocabulary reaches
 * this module; it knows axes and values, not products or capabilities.
 */

export type Point = {
  x: number;
  y: number;
};

export type RadarGeometry = {
  /** Where the wheel is pinned. The box need not be square, and rarely is. */
  centre: Point;
  /** Distance from the centre to the outermost ring. */
  radius: number;
  /** How many axes the wheel is divided into. */
  axes: number;
  /** The value the outermost ring stands for. */
  max: number;
};

/**
 * Where an axis points. The first axis stands straight up, which is what a
 * reader assumes when a chart has an obvious "top", and they run clockwise.
 */
export const axisAngle = (index: number, axes: number): number =>
  (index / axes) * 2 * Math.PI - Math.PI / 2;

/** A point on an axis at a given distance from the centre, 0 to 1. */
export const atRatio = (geometry: RadarGeometry, index: number, ratio: number): Point => {
  const angle = axisAngle(index, geometry.axes);
  const distance = geometry.radius * ratio;
  return {
    x: geometry.centre.x + Math.cos(angle) * distance,
    y: geometry.centre.y + Math.sin(angle) * distance,
  };
};

/** A point on an axis for a value on the chart's scale. Values clamp to the ring. */
export const atValue = (geometry: RadarGeometry, index: number, value: number): Point =>
  atRatio(geometry, index, geometry.max === 0 ? 0 : Math.min(Math.max(value, 0), geometry.max) / geometry.max);

/** The corners of one ring, ready to be joined into a polygon. */
export const ring = (geometry: RadarGeometry, ratio: number): Point[] =>
  Array.from({ length: geometry.axes }, (_, index) => atRatio(geometry, index, ratio));

export const pointsAttribute = (points: Point[]): string =>
  points.map((point) => `${round(point.x)},${round(point.y)}`).join(" ");

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * One unbroken stretch of a series: the axis indices it spans and the points to
 * draw. A run of a single point has no line, only a marker.
 */
export type Run = {
  indices: number[];
  points: Point[];
};

/**
 * A series split into the stretches that actually have values.
 *
 * A gap is not a zero, so the shape must break where a value is missing rather
 * than dive to the centre. When every axis has a value the result is one run
 * that wraps the whole wheel, and the caller can close it into a filled
 * polygon; anything less stays open, because a fill would claim an area the
 * data does not support.
 */
export const runsOf = (geometry: RadarGeometry, values: readonly (number | null)[]): Run[] => {
  const present = values.map((value, index) => ({ value, index })).filter((entry) => entry.value !== null);
  if (present.length === 0) return [];
  if (present.length === values.length) {
    return [
      {
        indices: values.map((_, index) => index),
        points: values.map((value, index) => atValue(geometry, index, value ?? 0)),
      },
    ];
  }

  /*
   * Rotating to the first axis that follows a gap makes the wheel behave like a
   * line: from there on, consecutive present values are genuinely adjacent, and
   * no run has to be stitched back together across the seam at index 0.
   */
  const start = values.findIndex((value, index) => value !== null && values[wrap(index - 1, values.length)] === null);
  const order = Array.from({ length: values.length }, (_, offset) => wrap(start + offset, values.length));

  const runs: Run[] = [];
  let current: Run | null = null;
  for (const index of order) {
    const value = values[index];
    if (value === null || value === undefined) {
      current = null;
      continue;
    }
    if (current === null) {
      current = { indices: [], points: [] };
      runs.push(current);
    }
    current.indices.push(index);
    current.points.push(atValue(geometry, index, value));
  }

  return runs;
};

/** True when a series covers every axis, so its shape can be closed and filled. */
export const isClosed = (values: readonly (number | null)[]): boolean =>
  values.length > 0 && values.every((value) => value !== null);

const wrap = (index: number, length: number): number => ((index % length) + length) % length;

/** Which side of the wheel a label sits on, so it can be anchored away from it. */
export const labelAnchor = (index: number, axes: number): "start" | "middle" | "end" => {
  const x = Math.cos(axisAngle(index, axes));
  if (Math.abs(x) < 0.1) return "middle";
  return x > 0 ? "start" : "end";
};
